import { memo, ReactElement, useEffect, useMemo } from 'react';

import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useData } from '../../contextProviders/DataProvider';
import useOrgsWithAdminRights from '../../dataHooks/useOrgsWithAdminRights';
import GratiSystemError from '../common/GratiSystemError';
import Hourglass from '../common/Hourglass';

export type OrgIdNamePair = {
  orgId: string;
  orgName: string;
}

export type OrgSelectorProps = {
  value: string | null;
  onChange: (orgPair: OrgIdNamePair) => void;
  disabled?: boolean;
  helperText?: string;
  alwaysShowDropdown?: boolean;
  allowNone?: boolean;
}

const OrgSelector = memo(function OrgSelector(props: OrgSelectorProps): ReactElement {
  const { value, onChange, disabled, helperText, alwaysShowDropdown = false, allowNone = false } = props;
  const { getAdminOrgs } = useData();
  const { orgs, isOrgsLoading, orgsError } = useOrgsWithAdminRights();

  const orgPairs = useMemo(() => {
    if (!orgs) return [];
    return orgs.map((org) => ({
      orgId: org.itemId,
      orgName: org.name,
    }));
  }, [orgs]);

  const adminOrgCount = getAdminOrgs().length;

  const isValuePresent = !value || orgPairs.some((orgPair) => orgPair.orgId === value);

  // Auto-select the first org if only one available and no value set
  useEffect(() => {
    if (orgPairs.length === 1 && !value) {
      onChange(orgPairs[0]);
    }
  }, [orgPairs, value, onChange]);

  // Clear the value if it no longer matches a loaded option (e.g. org was deleted
  // or admin rights were revoked) — otherwise MUI's Select renders an out-of-range value
  useEffect(() => {
    if (orgPairs.length > 0 && value && !isValuePresent) {
      onChange({ orgId: '', orgName: '' });
    }
  }, [orgPairs, value, isValuePresent, onChange]);

  if (isOrgsLoading && !orgs) {
    return <Hourglass />;
  }

  if (orgsError) {
    return <GratiSystemError error={orgsError} />;
  }

  if (!orgPairs?.length) {
    return <Typography>No organizations available</Typography>;
  }

  // If user only has one admin org, show as a disabled field (unless alwaysShowDropdown)
  if (adminOrgCount <= 1 && !alwaysShowDropdown) {
    return (
      <TextField
        id="org"
        label="Organization"
        value={orgPairs[0]?.orgName ?? ''}
        disabled
        fullWidth
      />
    );
  }

  return (
    <TextField
      id="org"
      label="Organization"
      required={!allowNone}
      fullWidth
      disabled={disabled}
      helperText={helperText}
      select
      aria-label="Organization"
      aria-required={!allowNone}
      value={isValuePresent ? (value ?? '') : ''}
      onChange={({ target }) => {
        if (target.value === '') {
          onChange({ orgId: '', orgName: '' });
          return;
        }
        const orgPair = orgPairs.find(orgPair => orgPair.orgId === target.value);
        if (orgPair) {
          onChange(orgPair);
        }
      }}
    >
      {allowNone && (
        <MenuItem key="" value="">
          None
        </MenuItem>
      )}
      {orgPairs.map((orgPair) => (
        <MenuItem key={orgPair.orgId} value={orgPair.orgId}>
          {orgPair.orgName}
        </MenuItem>
      ))}
    </TextField>
  );
});

export default OrgSelector;
